import { PROGRAM_TYPES } from 'utils/programHelpers'

/**
 * Unified function to check if a resource is complete based on instructor data
 * Handles both new and legacy data structures with comprehensive matching strategies
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data from Redux
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.program - The current program type
 * @param {string} [params.registrationId] - The registration ID from URL query params (optional for legacy compatibility)
 * @returns {boolean} - Whether the resource is complete
 */
export const isResourceComplete = ({
  instructorData,
  courseNodeId,
  program,
  registrationId = null,
}) => {
  if (!instructorData || !courseNodeId) {
    return false
  }

  // Check if instructorData has the new structure with educator and siteEducator arrays
  if (instructorData.educator || instructorData.siteEducator) {
    return checkNewStructure({
      instructorData,
      courseNodeId,
      registrationId,
    })
  }

  // Legacy structure: check program-specific data
  return checkLegacyStructure({
    instructorData,
    courseNodeId,
    program,
  })
}

/**
 * Check completion status using the new data structure
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.registrationId - The registration ID
 * @returns {boolean} - Whether the resource is complete
 */
const checkNewStructure = ({
  instructorData,
  courseNodeId,
  registrationId,
}) => {
  // If no registrationId, we can't determine completion status
  if (!registrationId) {
    return false
  }

  // New structure: look in siteEducator array for the matching registration_id
  const siteEducator = instructorData.siteEducator || []
  const courseInstance = siteEducator.find(
    course => String(course.registration_id) === String(registrationId),
  )

  if (!courseInstance?.lesson_nodes) {
    return false
  }

  // Find the right attempt in lesson_nodes that matches our courseNodeId
  const lessonNode = courseInstance.lesson_nodes.find(lesson => {
    // Check for exact match on course_node_id
    if (lesson.course_node_id === courseNodeId) {
      return true
    }
    // Check if leaf_node_id starts with the courseNodeId (handles cases with presentation IDs)
    if (lesson.leaf_node_id?.startsWith(courseNodeId)) {
      return true
    }
    // Check for exact match on leaf_node_id
    if (lesson.leaf_node_id === courseNodeId) {
      return true
    }
    return false
  })

  return lessonNode?.is_done || false
}

/**
 * Check completion status using the legacy data structure
 * @param {Object} params - Parameters object
 * @param {Object} params.instructorData - The instructor data
 * @param {string} params.courseNodeId - The course node ID to check
 * @param {string} params.program - The current program type
 * @returns {boolean} - Whether the resource is complete
 */
const checkLegacyStructure = ({ instructorData, courseNodeId, program }) => {
  // For tiered interventions, we need to check the appropriate instructor data
  let relevantInstructorData = null
  if (
    program === PROGRAM_TYPES.ELEMENTARY ||
    program === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    relevantInstructorData = instructorData?.K5SelDigital
  } else if (
    program === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    program === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    relevantInstructorData = instructorData?.MspDigital
  }

  if (!relevantInstructorData?.educator?.lesson_nodes) {
    return false
  }

  // Find the lesson node that matches our courseNodeId
  const lessonNode = relevantInstructorData.educator.lesson_nodes.find(
    lesson => lesson.course_node_id === courseNodeId,
  )

  return lessonNode?.is_done || false
}
